import { Routes } from '@angular/router';
import {
  authGuard,
  businessUserGuard,
  platformAdminGuard,
  businessSelectionGuard,
  posGuard,
  guestGuard,
} from './core/guards/auth.guard';
import { permissionGuard } from './core/guards/permission.guard';
import { subscriptionGuard } from './core/guards/subscription.guard';
import { planGuard } from './core/guards/plan.guard';
import { CanDeactivateGuard } from './features/products/product-import/can-deactivate.guard';

export const routes: Routes = [
  // Default redirect
  { path: '', redirectTo: '/dashboard', pathMatch: 'full' },

  // Public routes (only for unauthenticated users)
  {
    path: 'login',
    loadComponent: () =>
      import('./features/auth/login/login.component').then(m => m.LoginComponent),
    canActivate: [guestGuard],
  },
  {
    path: 'register',
    loadComponent: () =>
      import('./features/auth/register/register.component').then(m => m.RegisterComponent),
    canActivate: [guestGuard],
  },

  // Invite acceptance (only for unauthenticated users)
  {
    path: 'invite/:id',
    loadComponent: () =>
      import('./features/invite/accept-invite/accept-invite.component').then(
        m => m.AcceptInviteComponent
      ),
    canActivate: [guestGuard],
  },

  // User registration for invite acceptance (only for unauthenticated users)
  {
    path: 'join',
    loadComponent: () =>
      import('./features/invite/register-user/register-user.component').then(
        m => m.RegisterUserComponent
      ),
    canActivate: [guestGuard],
  },

  // Platform Admin routes (only for super admins)
  {
    path: 'platform-admin',
    loadComponent: () =>
      import('./features/platform-admin/platform-admin.component').then(
        m => m.PlatformAdminComponent
      ),
    canActivate: [platformAdminGuard],
    loadChildren: () =>
      import('./features/platform-admin/platform-admin.routes').then(m => m.PLATFORM_ADMIN_ROUTES),
  },

  // Business selection (for users with multiple businesses)
  {
    path: 'select-business',
    loadComponent: () =>
      import('./features/business/business-selector/business-selector.component').then(
        m => m.BusinessSelectorComponent
      ),
    canActivate: [authGuard, businessSelectionGuard],
  },

  // Suspended subscription page
  {
    path: 'suspended',
    loadComponent: () =>
      import('./features/subscription/suspended/suspended.component').then(
        m => m.SuspendedComponent
      ),
    canActivate: [authGuard],
  },

  // Business user routes (requires business selection + permissions + active subscription)
  {
    path: 'dashboard',
    loadComponent: () =>
      import('./features/dashboard/dashboard.component').then(m => m.DashboardComponent),
    canActivate: [businessUserGuard, subscriptionGuard, permissionGuard('canAccessDashboard')],
  },
  {
    path: 'facturar',
    loadComponent: () =>
      import('./features/invoicing/invoice-create.component').then(m => m.InvoiceCreateComponent),
    canActivate: [businessUserGuard, subscriptionGuard, permissionGuard('canCreateSale')],
    // S0-T5 / AC-6 / Q4: toggleable module (custom-plan override auto-detection)
    data: { toggleable: true, moduleId: 'invoicing', moduleLabel: 'Facturación' },
  },
  {
    path: 'pos',
    loadComponent: () => import('./features/sales/pos/pos.component').then(m => m.PosComponent),
    canActivate: [businessUserGuard, subscriptionGuard, permissionGuard('canAccessPOS'), posGuard],
    // S0-T5 / AC-6 / Q4: toggleable module (custom-plan override auto-detection)
    data: { toggleable: true, moduleId: 'pos', moduleLabel: 'Punto de Venta' },
  },
  {
    path: 'pos-settings',
    loadComponent: () =>
      import('./features/settings/pos-configuration/pos-configuration.component').then(
        m => m.PosConfigurationComponent
      ),
    canActivate: [
      businessUserGuard,
      subscriptionGuard,
      planGuard(['pay-caja', 'todo-incluido']),
      permissionGuard('canAccessPOSSettings'),
    ],
  },
  {
    path: 'cash-registers',
    loadComponent: () =>
      import('./features/cashier/cash-register.component').then(m => m.CashRegisterComponent),
    canActivate: [
      businessUserGuard,
      subscriptionGuard,
      planGuard(['pay-caja', 'todo-incluido']),
      permissionGuard('canAccessCashRegisters'),
    ],
    children: [
      // Routed dialog: `/cash-registers/:id` opens the existing
      // CashierDetailDialogComponent (the same modal the list uses on
      // click) over the list page. Stays as a child route so the list
      // remains mounted under the dialog and the user returns to it on
      // close. See cashier-detail-route-host.component.ts for mechanics.
      {
        path: ':id',
        loadComponent: () =>
          import('./features/cashier/components/cashier-detail-route-host.component').then(
            m => m.CashierDetailRouteHostComponent
          ),
      },
    ],
  },
  {
    path: 'payment-methods',
    loadComponent: () =>
      import('./features/sales/payment-methods/payment-methods.component').then(
        m => m.PaymentMethodsComponent
      ),
    canActivate: [
      businessUserGuard,
      subscriptionGuard,
      planGuard(['pay-caja', 'todo-incluido']),
      permissionGuard('canManagePaymentMethods'),
    ],
  },
  {
    path: 'products',
    loadComponent: () =>
      import('./features/products/product-list/product-list.component').then(
        m => m.ProductListComponent
      ),
    canActivate: [businessUserGuard, subscriptionGuard, permissionGuard('canAccessProducts')],
  },
  {
    path: 'products/new',
    loadComponent: () =>
      import('./features/products/product-create/product-create.component').then(
        m => m.ProductCreateComponent
      ),
    canActivate: [businessUserGuard, subscriptionGuard, permissionGuard('canCreateProduct')],
  },
  {
    path: 'products/view/:id',
    loadComponent: () =>
      import('./features/products/product-detail/product-detail.component').then(
        m => m.ProductDetailComponent
      ),
    canActivate: [businessUserGuard, subscriptionGuard, permissionGuard('canAccessProducts')],
  },
  {
    path: 'products/edit/:id',
    loadComponent: () =>
      import('./features/products/product-create/product-create.component').then(
        m => m.ProductCreateComponent
      ),
    canActivate: [businessUserGuard, subscriptionGuard, permissionGuard('canEditProduct')],
  },
  {
    path: 'products/import',
    loadComponent: () =>
      import('./features/products/product-import/product-import.component').then(
        m => m.ProductImportComponent
      ),
    canActivate: [businessUserGuard, subscriptionGuard, permissionGuard('canImportProducts')],
    canDeactivate: [CanDeactivateGuard],
  },
  {
    path: 'products/categories',
    loadComponent: () =>
      import('./features/products/category-management/category-management.component').then(
        m => m.CategoryManagementComponent
      ),
    canActivate: [businessUserGuard, subscriptionGuard, permissionGuard('canManageCategories')],
  },
  {
    path: 'products/variants',
    loadComponent: () =>
      import('./features/products/variant-management/variant-management.component').then(
        m => m.VariantManagementComponent
      ),
    canActivate: [businessUserGuard, subscriptionGuard, permissionGuard('canManageVariants')],
  },
  {
    path: 'locations',
    loadComponent: () =>
      import('./features/locations/location-list/location-list.component').then(
        m => m.LocationListComponent
      ),
    canActivate: [
      businessUserGuard,
      subscriptionGuard,
      planGuard(['pay-caja', 'todo-incluido']),
      permissionGuard('canAccessLocations'),
    ],
  },
  {
    path: 'sales',
    loadComponent: () =>
      import('./features/sales/sale-list/sale-list.component').then(m => m.SaleListComponent),
    canActivate: [businessUserGuard, subscriptionGuard, permissionGuard('canAccessSales')],
  },
  {
    path: 'sales/accounts-receivable',
    loadComponent: () =>
      import('./features/sales/accounts-receivable/accounts-receivable.component').then(
        m => m.AccountsReceivableComponent
      ),
    canActivate: [
      businessUserGuard,
      subscriptionGuard,
      permissionGuard('canViewAccountsReceivable'),
    ],
  },
  // Declared BEFORE `sales/:id` on purpose: that wildcard would otherwise swallow
  // this path and try to open a sale whose id is "price-lists".
  {
    path: 'sales/price-lists',
    loadComponent: () =>
      import('./features/sales/price-lists/price-lists.component').then(m => m.PriceListsComponent),
    canActivate: [businessUserGuard, subscriptionGuard, permissionGuard('canManagePriceLists')],
  },
  {
    path: 'sales/credit/:id',
    loadComponent: () =>
      import('./features/sales/credit-order-detail/credit-order-detail.component').then(
        m => m.CreditOrderDetailComponent
      ),
    canActivate: [businessUserGuard, subscriptionGuard, permissionGuard('canAccessSales')],
  },
  {
    path: 'sales/:id',
    loadComponent: () =>
      import('./features/sales/sale-detail/sale-detail.component').then(m => m.SaleDetailComponent),
    canActivate: [businessUserGuard, subscriptionGuard, permissionGuard('canAccessSales')],
  },
  {
    path: 'inventory/movements',
    loadComponent: () =>
      import('./features/inventory/stock-movements/stock-movements.component').then(
        m => m.StockMovementsComponent
      ),
    canActivate: [
      businessUserGuard,
      subscriptionGuard,
      planGuard(['pay-caja', 'todo-incluido']),
      permissionGuard('canViewMovementHistory'),
    ],
    // S0-T5 / AC-6 / Q4: toggleable module (custom-plan override auto-detection).
    // Representative route for the "inventory" module (no single parent route exists).
    data: { toggleable: true, moduleId: 'inventory', moduleLabel: 'Inventario' },
  },
  {
    path: 'inventory/restocking',
    loadComponent: () =>
      import('./features/inventory/restocking/restocking.component').then(
        m => m.RestockingComponent
      ),
    canActivate: [
      businessUserGuard,
      subscriptionGuard,
      planGuard(['pay-caja', 'todo-incluido']),
      permissionGuard('canReceiveStock'),
    ],
  },
  {
    path: 'inventory/stock-take',
    loadComponent: () =>
      import('./features/inventory/stock-take/stock-take.component').then(
        m => m.StockTakeComponent
      ),
    canActivate: [
      businessUserGuard,
      subscriptionGuard,
      planGuard(['pay-caja', 'todo-incluido']),
      permissionGuard('canPerformStockTake'),
    ],
  },
  {
    path: 'inventory/transfers',
    loadComponent: () =>
      import('./features/inventory/transfers/transfers.component').then(m => m.TransfersComponent),
    canActivate: [
      businessUserGuard,
      subscriptionGuard,
      planGuard(['pay-caja', 'todo-incluido']),
      permissionGuard('canTransferStock'),
    ],
    loadChildren: () =>
      import('./features/inventory/transfers/transfers.routes').then(m => m.TRANSFERS_ROUTES),
  },
  // Purchases Module
  {
    path: 'purchases',
    loadComponent: () =>
      import('./features/purchases/purchase-list/purchase-list.component').then(
        m => m.PurchaseListComponent
      ),
    canActivate: [
      businessUserGuard,
      subscriptionGuard,
      planGuard(['pay-caja', 'todo-incluido']),
      permissionGuard('canAccessPurchases'),
    ],
    // S0-T5 / AC-6 / Q4: toggleable module (custom-plan override auto-detection)
    data: { toggleable: true, moduleId: 'purchasing', moduleLabel: 'Compras' },
  },
  {
    path: 'purchases/new',
    loadComponent: () =>
      import('./features/purchases/purchase-form/purchase-form.component').then(
        m => m.PurchaseFormComponent
      ),
    canActivate: [
      businessUserGuard,
      subscriptionGuard,
      planGuard(['pay-caja', 'todo-incluido']),
      permissionGuard('canCreatePurchaseOrder'),
    ],
    canDeactivate: [CanDeactivateGuard],
  },
  {
    path: 'purchases/suppliers',
    loadComponent: () =>
      import('./features/purchases/suppliers/supplier-list.component').then(
        m => m.SupplierListComponent
      ),
    canActivate: [
      businessUserGuard,
      subscriptionGuard,
      planGuard(['pay-caja', 'todo-incluido']),
      permissionGuard('canManageSuppliers'),
    ],
  },
  {
    path: 'purchases/accounts-payable',
    loadComponent: () =>
      import('./features/purchases/accounts-payable/accounts-payable.component').then(
        m => m.AccountsPayableComponent
      ),
    canActivate: [
      businessUserGuard,
      subscriptionGuard,
      planGuard(['pay-caja', 'todo-incluido']),
      permissionGuard('canViewAccountsPayable'),
    ],
  },
  {
    path: 'purchases/payable/:id',
    loadComponent: () =>
      import('./features/purchases/payable-detail/payable-detail.component').then(
        m => m.PayableDetailComponent
      ),
    canActivate: [
      businessUserGuard,
      subscriptionGuard,
      planGuard(['pay-caja', 'todo-incluido']),
      permissionGuard('canViewAccountsPayable'),
    ],
  },
  {
    path: 'purchases/:id',
    loadComponent: () =>
      import('./features/purchases/purchase-detail/purchase-detail.component').then(
        m => m.PurchaseDetailComponent
      ),
    canActivate: [
      businessUserGuard,
      subscriptionGuard,
      planGuard(['pay-caja', 'todo-incluido']),
      permissionGuard('canViewPurchaseOrders'),
    ],
  },
  {
    path: 'purchases/:id/edit',
    loadComponent: () =>
      import('./features/purchases/purchase-form/purchase-form.component').then(
        m => m.PurchaseFormComponent
      ),
    canActivate: [
      businessUserGuard,
      subscriptionGuard,
      planGuard(['pay-caja', 'todo-incluido']),
      permissionGuard('canEditPurchaseOrder'),
    ],
    canDeactivate: [CanDeactivateGuard],
  },
  {
    path: 'purchases/:id/receive',
    loadComponent: () =>
      import('./features/purchases/purchase-receive/purchase-receive.component').then(
        m => m.PurchaseReceiveComponent
      ),
    canActivate: [
      businessUserGuard,
      subscriptionGuard,
      planGuard(['pay-caja', 'todo-incluido']),
      permissionGuard('canReceivePurchaseOrder'),
    ],
  },
  {
    path: 'clients',
    loadComponent: () =>
      import('./features/clients/client-list/client-list.component').then(
        m => m.ClientListComponent
      ),
    canActivate: [businessUserGuard, subscriptionGuard, permissionGuard('canAccessCustomers')],
  },
  {
    path: 'clients/new',
    loadComponent: () =>
      import('./features/clients/client-form/client-form.component').then(
        m => m.ClientFormComponent
      ),
    canActivate: [businessUserGuard, subscriptionGuard, permissionGuard('canCreateCustomer')],
  },
  {
    path: 'clients/import',
    loadComponent: () =>
      import('./features/clients/client-import/client-import.component').then(
        m => m.ClientImportComponent
      ),
    canActivate: [businessUserGuard, subscriptionGuard, permissionGuard('canCreateCustomer')],
  },
  {
    path: 'clients/:id',
    loadComponent: () =>
      import('./features/clients/client-profile/client-profile.component').then(
        m => m.ClientProfileComponent
      ),
    canActivate: [businessUserGuard, subscriptionGuard, permissionGuard('canViewCustomers')],
  },
  {
    path: 'clients/edit/:id',
    loadComponent: () =>
      import('./features/clients/client-form/client-form.component').then(
        m => m.ClientFormComponent
      ),
    canActivate: [businessUserGuard, subscriptionGuard, permissionGuard('canEditCustomer')],
  },
  {
    path: 'employees',
    canActivate: [
      businessUserGuard,
      subscriptionGuard,
      planGuard(['pay-caja', 'todo-incluido']),
      permissionGuard('canAccessEmployees'),
    ],
    // S0-T5 / AC-6 / Q4: toggleable module (custom-plan override auto-detection)
    data: { toggleable: true, moduleId: 'employees', moduleLabel: 'Empleados' },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/employees/employee-list/employee-list.component').then(
            m => m.EmployeeListComponent
          ),
      },
      {
        path: 'salaries',
        loadComponent: () =>
          import('./features/employees/employee-salary-list/employee-salary-list.component').then(
            m => m.EmployeeSalaryListComponent
          ),
        canActivate: [permissionGuard('canViewSalaries')],
      },
    ],
  },
  // Expenses Module
  {
    path: 'expenses',
    canActivate: [
      businessUserGuard,
      subscriptionGuard,
      planGuard(['pay-caja', 'todo-incluido']),
      permissionGuard('canAccessExpenses'),
    ],
    // S0-T5 / AC-6 / Q4: toggleable module (custom-plan override auto-detection)
    data: { toggleable: true, moduleId: 'expenses', moduleLabel: 'Gastos' },
    loadChildren: () => import('./features/expenses/expenses.routes').then(m => m.EXPENSES_ROUTES),
  },

  {
    path: 'reports',
    loadComponent: () =>
      import('./features/reports/reports.component').then(m => m.ReportsComponent),
    canActivate: [
      businessUserGuard,
      subscriptionGuard,
      planGuard(['pay-caja', 'todo-incluido']),
      permissionGuard('canAccessReports'),
    ],
    // S0-T5 / AC-6 / Q4: toggleable module (custom-plan override auto-detection)
    data: { toggleable: true, moduleId: 'reports', moduleLabel: 'Reportes' },
    loadChildren: () => import('./features/reports/reports.routes').then(m => m.REPORTS_ROUTES),
  },

  // Settings Module
  {
    path: 'settings',
    loadComponent: () =>
      import('./features/settings/settings.component').then(m => m.SettingsComponent),
    canActivate: [businessUserGuard, permissionGuard('canAccessSettings')],
    loadChildren: () => import('./features/settings/settings.routes').then(m => m.SETTINGS_ROUTES),
  },

  // Profile (any authenticated business user)
  {
    path: 'profile',
    loadComponent: () =>
      import('./features/profile/profile.component').then(m => m.ProfileComponent),
    canActivate: [businessUserGuard],
  },

  // Error pages
  {
    path: 'unauthorized',
    loadComponent: () =>
      import('./shared/components/unauthorized/unauthorized.component').then(
        m => m.UnauthorizedComponent
      ),
  },

  // Fallback
  { path: '**', redirectTo: '/dashboard' },
];
